import { Text } from "@solomon-apps/shared/src/components/ui/text";
import {
    FieldRow,
    SectionHeading,
    SectionStack
} from "@solomon-labs/design/components/layout";
import type { StatusBadgeTone } from "@solomon-labs/design/components/ui";
import {
    IndicationUnit,
    StatusBadge
} from "@solomon-labs/design/components/ui";
import type { ReactElement } from "react";

import type {
    AgreementEnvelopeStatus,
    AgreementEnvelopeView,
    AgreementType
} from "@/services/clientOnboarding";

const AGREEMENT_TITLE: Record<AgreementType, string> = {
    msa: "Master Services Agreement",
    minting_master: "Minting Master Agreement"
};

/**
 * Each envelope stage as a design-system `StatusBadge` tone plus its label.
 *
 * The tone carries the "is this on me?" reading, which is what a client
 * scanning the list actually wants:
 *
 *   - `warning` — the signatory has it and it is unsigned. The only rows that
 *     are waiting on the client.
 *   - `success` — the client's part is done; Solomon owes the counter-signature.
 *   - `info` — finished, nothing to read into it.
 *   - `error` — the send failed or the agreement will not complete as-is, and
 *     the issuer has to send a new one.
 *   - `pending` — prepared but not yet sent, so nothing has reached an inbox.
 *
 * `sent` and `delivered` share a badge on purpose: whether the signatory has
 * opened the email changes nothing about what they must do, and a client
 * cannot act on the difference.
 */
const ENVELOPE_STAGE_BADGE: Record<
    AgreementEnvelopeStatus,
    { tone: StatusBadgeTone; label: string }
> = {
    created: { tone: "pending", label: "Being Prepared" },
    sent: { tone: "warning", label: "Awaiting Signature" },
    delivered: { tone: "warning", label: "Awaiting Signature" },
    client_signed: {
        tone: "success",
        label: "Signed. Awaiting Counter-signature"
    },
    signed: { tone: "info", label: "Completed" },
    declined: { tone: "error", label: "Declined" },
    voided: { tone: "error", label: "Withdrawn" },
    email_bounced: { tone: "error", label: "Email Not Delivered" }
};

/**
 * The one line that tells the client what to do next.
 *
 * Both nouns track the envelope count, so a single-agreement client is never
 * told to sign "agreements" it does not have. Anything other than exactly one
 * takes the plural: two or more obviously, and zero because the envelopes are
 * still being prepared and more than one may arrive.
 *
 * Docusign is named because that is what lands in the inbox — a client
 * scanning for the sender finds it faster than they find "Solomon".
 */
/** Envelope stages where the agreement will not complete without a new send. */
const STALLED_STAGES: ReadonlySet<AgreementEnvelopeStatus> = new Set([
    "declined",
    "voided",
    "email_bounced"
]);

/**
 * What the rows collectively mean, which is what the sentence above them has
 * to agree with.
 *
 * The sentence used to read off the envelope COUNT alone, so it said "sent …
 * please sign" at every stage: it claimed a send that had not happened yet on
 * `created` (whose `sentAt` is null), and it went on asking for a signature
 * under rows that already read "Completed", or that had failed and cannot be
 * signed at all. The stage has to reach the copy or the two contradict each
 * other on the same screen.
 */
type AgreementsPhase = "preparing" | "stalled" | "awaiting" | "complete";

export function agreementsPhase(
    statuses: readonly AgreementEnvelopeStatus[]
): AgreementsPhase {
    if (statuses.length === 0) return "preparing";
    // A failure outranks the rest: it is the only thing the client can act on,
    // and it is resolved by the issuer sending again, not by signing.
    if (statuses.some((status) => STALLED_STAGES.has(status))) return "stalled";
    if (statuses.every((status) => status === "signed")) return "complete";
    if (statuses.every((status) => status === "created")) return "preparing";
    return "awaiting";
}

export function agreementsOutMessage(
    signatoryEmail: string | null,
    agreementCount: number,
    mintingOnly = false,
    phase: AgreementsPhase = "awaiting"
): string {
    const recipient = signatoryEmail ?? "your authorized signatory";
    // A Market Maker signs the minting agreement alone, so that branch is
    // always singular. Named exactly as its row is titled — the body used to
    // say "Minting Agreement" while the row read "Minting Master Agreement",
    // which reads as two documents.
    const noun =
        mintingOnly || agreementCount === 1 ? "agreement" : "agreements";
    // The noun opens the sentence, so it carries a capital in the first
    // clause and not in the second.
    const Noun = `${noun[0].toUpperCase()}${noun.slice(1)}`;
    const subject = mintingOnly ? "Minting Master Agreement" : Noun;

    // The trailing sentence exists so a Market Maker does not read the
    // signature as the end of the mint and redeem setup.
    const mintingNote = mintingOnly
        ? " Solomon coordinates any external USDv mint and redeem enablement separately."
        : "";

    if (phase === "preparing") {
        return `${subject} being prepared for ${recipient}. Nothing to sign yet.${mintingNote}`;
    }
    if (phase === "stalled") {
        return `${subject} sent to ${recipient} (through Docusign) could not be completed. Solomon will send a new one.${mintingNote}`;
    }
    if (phase === "complete") {
        return `${subject} signed. Your console opens shortly.${mintingNote}`;
    }
    return `${subject} sent to ${recipient} (through Docusign). Please sign the ${noun} to proceed.${mintingNote}`;
}

/** The stage badge for one envelope. Shared by both width branches below. */
function AgreementStatusBadge({
    status
}: {
    status: AgreementEnvelopeStatus;
}): ReactElement {
    return (
        <StatusBadge
            variant="plain"
            size="lg"
            // `size` fuses the type scale to a filled pill's box and `plain`
            // drops the fill but keeps the box. Zero it so the label sits on
            // the row's own edge.
            className="p-0"
            tone={ENVELOPE_STAGE_BADGE[status].tone}
        >
            {ENVELOPE_STAGE_BADGE[status].label}
        </StatusBadge>
    );
}

/**
 * Onboarding "agreements out" state, shown once the issuer has approved a
 * lite application and the agreements are out for signature (status
 * `awaiting_agreements`). The full hub tracks this on its agreement rows;
 * the lite page has no rail, so this card carries the same facts: the
 * application is approved, the next action is in the signatory's inbox, and
 * where each agreement stands. No CTA: nothing on this page can advance it.
 * Mirrors `PendingReviewCard`'s layout so the two read as one sequence.
 */
export function AgreementsOutCard({
    signatoryEmail,
    agreements,
    mintingOnly = false
}: {
    signatoryEmail: string | null;
    agreements: AgreementEnvelopeView[];
    mintingOnly?: boolean;
}): ReactElement {
    return (
        <div className="mt-25">
            <section className="flex justify-center">
                {/*
                 * Two gaps, each owned by the container it belongs
                 * to, rather than per-child margins nobody can find.
                 * The heading and the line under it are ONE unit: the
                 * message says what the approval means, so it belongs
                 * to the heading, not to the agreements. The wider
                 * step below separates that unit from the content,
                 * and the difference between the two is the
                 * hierarchy.
                 */}
                <div className="flex flex-col gap-20 px-12 py-12 w-full max-w-2xl text-center">
                    <div className="flex flex-col gap-12">
                        <div className="flex items-start justify-center gap-2">
                            <IndicationUnit
                                variant="connect"
                                state="connecting"
                                pairWith="h1"
                            />
                            <Text
                                variant="h1"
                                render={<h1 />}
                                className="text-foreground-base"
                            >
                                Your application is approved
                            </Text>
                        </div>
                        <Text
                            variant="body"
                            className="block text-foreground-base"
                        >
                            {agreementsOutMessage(
                                signatoryEmail,
                                agreements.length,
                                mintingOnly,
                                agreementsPhase(
                                    agreements.map(
                                        (agreement) => agreement.status
                                    )
                                )
                            )}
                        </Text>
                    </div>
                    {agreements.length > 0 ? (
                        <div className="text-left">
                            {/*
                             * `SectionHeading` owns the heading and the rule
                             * beneath it, so the block has a top edge that is a
                             * heading separator rather than a terminal rule.
                             * That is what lets ONE agreement sit inside
                             * something instead of floating: the anchor comes
                             * from the heading, not from bracketing the rows.
                             *
                             * `variant="body"` sits the heading a step above
                             * the rows it labels and stays inside the page's
                             * Editorial working set — the h2/h3/label variants
                             * are Console-only, so the element is an h2 (the
                             * list stays reachable by heading navigation)
                             * while the type is not.
                             */}
                            <SectionHeading variant="body">
                                Agreements
                            </SectionHeading>
                            {/*
                             * `SectionStack` takes no padding by construction
                             * and draws rules only between siblings, so the
                             * N+1-rule and inset-rule defects cannot be
                             * expressed here. Rows carry no divider of their
                             * own: each `FieldRow` leader connects its
                             * agreement to its status, which is what stops a
                             * lone row reading as unanchored.
                             */}
                            <SectionStack as="ul" gap="2xs" className="pt-3">
                                {agreements.map((agreement) => (
                                    <li key={agreement.uuid}>
                                        <FieldRow
                                            // `FieldRow` does not wrap, and its
                                            // value is `shrink-0`: at 375px a
                                            // 226px status label ("Signed.
                                            // Awaiting Counter-signature")
                                            // crushed the agreement name to a
                                            // 5px column over three lines.
                                            // Allowing a wrap lets the status
                                            // drop to its own line instead,
                                            // which keeps ONE row in the DOM
                                            // rather than a hidden second copy
                                            // per breakpoint.
                                            className="flex-wrap gap-y-1"
                                            // `md` maps the label to `body`
                                            // (18px), which rendered the
                                            // agreement name larger than both
                                            // its own section heading and its
                                            // status. `sm` puts the label on
                                            // `small`, so the whole block sits
                                            // on one working size.
                                            size="sm"
                                            connector="gap"
                                            label={
                                                AGREEMENT_TITLE[
                                                    agreement.agreementType
                                                ]
                                            }
                                            value={
                                                <AgreementStatusBadge
                                                    status={agreement.status}
                                                />
                                            }
                                        />
                                    </li>
                                ))}
                            </SectionStack>
                        </div>
                    ) : null}
                </div>
            </section>
        </div>
    );
}
